/* eslint-disable max-lines */
import { type CancellationProtection } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import type { BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import constate from "constate";
import { useEffect, useMemo, useState } from "react";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { useGetWorkplace } from "../../Facilities/api/useGetWorkplace";
import { type ShiftDiscoverySearchMode } from "../../OpenShifts/ShiftAction/api/useClaimShift";
import { useGetBookabilityStatusForShifts } from "../../OpenShifts/ShiftAction/api/useGetBookabilityStatusForShifts";
import { type ComputedArrivalData } from "../ArrivalTime/computeArrivalData";
import { type ArrivalTimePoints } from "../ArrivalTime/useArrivalTimePoints";
import { useCredentialRequirementConfiguration } from "../Documents/useCredentialRequirementConfiguration";
import { getBlockingRequirementsContext } from "../Shift/Bookability/Documents/getBlockingRequirementsContext";
import { type BlockingRequirementsContext } from "../Shift/Bookability/Documents/types";
import { useBundleForShift } from "../Shift/Bundle/hooks/useBundleForShift";
import { type BundleShiftPay, type ShiftBundle } from "../Shift/Bundle/types";
import { useDoubleForShift } from "../Shift/Double/hooks/useDoubleForShift";
import { type ShiftDouble } from "../Shift/Double/types";
import { type ShiftSlotCoalesced } from "../Shift/Open/useGetShiftsSlotV2";
import type { ShiftBoost } from "../Shift/ShiftBoost/api/schema";
import { type ModalShiftData } from "../Shift/types";
import { type WorkerPublicProfile } from "../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { getShiftModalRouteParams } from "./getShiftModalRouteParams";
import { useGetModalShiftData } from "./useGetModalShiftData";

interface UseShiftModalsDataProps {
  /**
   * Base path used for navigation when opening/closing facility modals.
   * This path is used to:
   * 1. Determine where to navigate when closing modals
   * 2. Support rendering the same modals from different root views (map/list)
   */
  baseNavigationPath: string;

  /**
   * Search mode used by underlying components to identify which mode was used to open shift modals.
   */
  searchMode: ShiftDiscoverySearchMode;
}

/**
 * Used to pre-populate shift modals with data so that the modals can be rendered immediately.
 * The data is refreshed by the useGetModalShiftData when the modals are opened.
 */
interface InitialShiftModalsData {
  shift?: ModalShiftData;
  availableShiftBoost?: ShiftBoost;
  appliedShiftBoost?: ShiftBoost;
  bookabilityStatus?: BookabilityStatusItem;
  resolvedShiftQualification?: string;
  hasHoursRestrictionConflict?: boolean;
  blockingRequirementsContext?: BlockingRequirementsContext;
  shiftSlots?: ShiftSlotCoalesced;
  friendsMap?: Map<string, WorkerPublicProfile>;
  recentColleaguesMap?: Map<string, RecentColleague>;
  outboundFriendRequestsSet?: Set<string>;
  isIndependentContractorBadgesRequired?: boolean;
  cancellationProtection?: CancellationProtection;
  computedArrivalData?: ComputedArrivalData;
  arrivalTimePoints?: ArrivalTimePoints;
  hasPreShiftOrientation?: boolean;
  preShiftOrientationAdjustedStart?: Date;
  preShiftOrientationLengthInMinutes?: number;
  bundle?: ShiftBundle;
  /** Results from booking bundle shifts, set after a bundle booking attempt */
  bundleBookingResults?: { bookedShiftIds: string[]; failedShiftIds: string[] };
  bundleShiftPays?: Map<string, BundleShiftPay>;
  double?: ShiftDouble;
  isForYouShift?: boolean;
  isTopThreeShift?: boolean;
  /** Bookability statuses for companion shifts in a double, keyed by shift ID */
  companionBookabilityStatuses?: Map<string, BookabilityStatusItem>;
  /** Results from booking double shifts, set after a double booking attempt */
  doubleBookingResults?: { bookedShiftIds: string[]; failedShiftIds: string[] };
}

const InitialShiftModalsDataValue: InitialShiftModalsData = {
  shift: undefined,
  availableShiftBoost: undefined,
  appliedShiftBoost: undefined,
  bookabilityStatus: undefined,
  hasHoursRestrictionConflict: undefined,
  blockingRequirementsContext: undefined,
  outboundFriendRequestsSet: undefined,
  isIndependentContractorBadgesRequired: false,
  cancellationProtection: undefined,
  computedArrivalData: undefined,
  arrivalTimePoints: undefined,
  hasPreShiftOrientation: false,
  preShiftOrientationAdjustedStart: undefined,
  preShiftOrientationLengthInMinutes: undefined,
  bundle: undefined,
  bundleBookingResults: undefined,
  bundleShiftPays: undefined,
  double: undefined,
  companionBookabilityStatuses: undefined,
  doubleBookingResults: undefined,
  isForYouShift: false,
  isTopThreeShift: false,
};

/**
 * This context manages the state for shift modals with these key features:
 * 1. Path generation - Handles both direct and nested modal paths
 * 2. Base path override - Supports rendering on top of workplace modals
 *
 * Modal Hierarchy:
 * - Shift modals can appear independently or on top of workplace modals
 * - When appearing on top of workplace modals, baseNavigationPathOverride is used
 * - This allows the workplace modal to stay mounted while shift modals animate in/out
 *
 * Usage:
 * - Direct modal: uses baseNavigationPath
 * - Nested modal: uses baseNavigationPath + baseNavigationPathOverride
 */
function useShiftModalsData(props: UseShiftModalsDataProps) {
  const { baseNavigationPath, searchMode } = props;

  /**
   * When shift modals need to open on top of an existing modal (e.g. workplace open shifts modal),
   * we override the base navigation path. This ensures the new modal is rendered in addition to
   * the currently visible base modal, rather than replacing it.
   */
  const [baseNavigationPathOverride, setBaseNavigationPathOverride] = useState<string>();

  const history = useHistory();
  const worker = useDefinedWorker();
  const location = useLocation();

  const navigateToModal = <S extends string>(
    path: S,
    options?: {
      pathParams?: Parameters<typeof generatePath<S>>[1];
      searchParams?: Record<string, string>;
      state?: Record<string, string>;
      clearExistingSearchParams?: boolean;
      forceReplacePath?: boolean;
    }
  ) => {
    const searchParams = new URLSearchParams(
      options?.clearExistingSearchParams === true ? undefined : location.search
    );

    if (options?.searchParams) {
      Object.entries(options.searchParams).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
    }

    const modalPath = generatePath(
      `${baseNavigationPath}${
        baseNavigationPathOverride ? `/${baseNavigationPathOverride}` : ""
      }/${path}`,
      options?.pathParams as unknown as
        | Parameters<typeof generatePath<`${string}/${S}`>>[1]
        | undefined
    );

    const finalPath = searchParams.size > 0 ? `${modalPath}?${searchParams.toString()}` : modalPath;

    if (isDefined(baseNavigationPathOverride) || options?.forceReplacePath) {
      history.replace(finalPath, ...(options?.state ? [options.state] : []));
    } else {
      history.push(finalPath, ...(options?.state ? [options.state] : []));
    }
  };

  const { pathname } = useLocation();

  const isNestedModalContext = isDefined(baseNavigationPathOverride);
  const modalRouteParams = getShiftModalRouteParams(
    isNestedModalContext
      ? `${baseNavigationPath}/${baseNavigationPathOverride}`
      : baseNavigationPath,
    pathname
  );

  const shiftId = modalRouteParams?.shiftId;

  const [initialShiftModalsData, setInitialShiftModalsData] = useState<InitialShiftModalsData>(
    InitialShiftModalsDataValue
  );

  const initialDataIsStale =
    isDefined(shiftId) &&
    isDefined(initialShiftModalsData.shift) &&
    initialShiftModalsData.shift.id !== shiftId;

  useEffect(() => {
    if (initialDataIsStale) {
      setInitialShiftModalsData(InitialShiftModalsDataValue);
    }
  }, [initialDataIsStale, setInitialShiftModalsData]);

  const { data, isLoading, shiftFailedLoading, shiftFailedReasons, stateTransitionTimings } =
    useGetModalShiftData({
      shiftId,
    });

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const shift = data?.shift ?? initialShiftModalsData?.shift;
  const availableShiftBoost =
    data?.availableShiftBoost ?? initialShiftModalsData?.availableShiftBoost;
  const appliedShiftBoost = data?.appliedShiftBoost ?? initialShiftModalsData?.appliedShiftBoost;
  const bookabilityStatus = data?.bookabilityStatus ?? initialShiftModalsData?.bookabilityStatus;
  const resolvedShiftQualification = data?.resolvedShiftQualification;
  const shiftSlots = data?.shiftSlots ?? initialShiftModalsData?.shiftSlots;
  const friendsMap = data?.friendsMap ?? initialShiftModalsData?.friendsMap;
  const recentColleaguesMap =
    data?.recentColleaguesMap ?? initialShiftModalsData?.recentColleaguesMap;
  const hasHoursRestrictionConflict =
    data?.hasHoursRestrictionConflict ?? initialShiftModalsData?.hasHoursRestrictionConflict;
  const outboundFriendRequestsSet =
    data?.outboundFriendRequestsSet ?? initialShiftModalsData?.outboundFriendRequestsSet;
  const computedArrivalData =
    data?.computedArrivalData ?? initialShiftModalsData?.computedArrivalData;
  const arrivalTimePoints = data?.arrivalTimePoints ?? initialShiftModalsData?.arrivalTimePoints;
  const hasPreShiftOrientation =
    data?.hasPreShiftOrientation ?? initialShiftModalsData?.hasPreShiftOrientation ?? false;
  const preShiftOrientationAdjustedStart =
    data?.preShiftOrientationAdjustedStart ??
    initialShiftModalsData?.preShiftOrientationAdjustedStart;
  const preShiftOrientationLengthInMinutes =
    data?.preShiftOrientationLengthInMinutes ??
    initialShiftModalsData?.preShiftOrientationLengthInMinutes;

  const isBundleAlreadyInContextData = isDefined(initialShiftModalsData?.bundle);
  const { bundle: fallbackRecalculatedBundle, isLoading: isBundleLoading } = useBundleForShift({
    shiftId,
    workplace: shift?.workplace,
    enabled: !isBundleAlreadyInContextData && isDefined(shift),
  });

  const bundle = initialShiftModalsData?.bundle ?? fallbackRecalculatedBundle;
  const bundleBookingResults = initialShiftModalsData?.bundleBookingResults;
  const bundleShiftPays = initialShiftModalsData?.bundleShiftPays;

  const isDoubleAlreadyInContextData = isDefined(initialShiftModalsData?.double);
  const { double: fallbackRecalculatedDouble, isLoading: isDoubleLoading } = useDoubleForShift({
    shiftId,
    workplace: shift?.workplace,
    enabled: !isDoubleAlreadyInContextData && isDefined(shift),
  });

  const double = initialShiftModalsData?.double ?? fallbackRecalculatedDouble;
  const doubleBookingResults = initialShiftModalsData?.doubleBookingResults;

  // Fallback: fetch companion bookability when returning from quiz (context was lost on remount)
  const hasCompanionBookabilityInContext = isDefined(
    initialShiftModalsData?.companionBookabilityStatuses
  );
  const companionShiftIds = useMemo(
    () => double?.companionShifts.map((c) => c.id) ?? [],
    [double?.companionShifts]
  );
  const { shiftBookabilityById: companionBookabilityById } = useGetBookabilityStatusForShifts(
    {
      shiftIds: companionShiftIds,
      agentId: worker.userId,
      returnFirstUnmetCriteria: false,
    },
    {
      enabled:
        !hasCompanionBookabilityInContext && companionShiftIds.length > 0 && isDefined(double),
    }
  );
  const fallbackCompanionBookabilityStatuses = useMemo(() => {
    if (hasCompanionBookabilityInContext || companionShiftIds.length === 0) {
      return undefined;
    }
    const entries = companionShiftIds
      .map((id) => {
        const status = companionBookabilityById[id];
        return status ? ([id, status] as const) : undefined;
      })
      .filter(isDefined);
    return entries.length > 0 ? new Map(entries) : undefined;
  }, [hasCompanionBookabilityInContext, companionShiftIds, companionBookabilityById]);

  const companionBookabilityStatuses =
    initialShiftModalsData?.companionBookabilityStatuses ?? fallbackCompanionBookabilityStatuses;

  const blockingRequirementsContext = isDefined(data?.blockingRequirements)
    ? getBlockingRequirementsContext({
        blockingRequirements: data.blockingRequirements,
        credentialRequirementConfiguration,
      })
    : initialShiftModalsData?.blockingRequirementsContext;

  const workplaceId = shift?.workplace.id ?? initialShiftModalsData?.shift?.workplace.id;
  const { data: workplaceProfile, isLoading: isLoadingWorkplace } = useGetWorkplace(
    { workplaceId },
    { enabled: isDefined(workplaceId) }
  );

  const isIndependentContractorBadgesRequired =
    workplaceProfile?.data.attributes.independentContractorBadge.enabled ?? false;
  const cancellationProtection = workplaceProfile?.data.attributes.cancellationProtection;
  const isCancellationProtectionPremiumApplied =
    shift?.offer?.attributes.isCancellationProtectionPremiumApplied;

  return {
    baseNavigationPath,
    baseNavigationPathOverride,
    blockingRequirementsContext,
    bookabilityStatus,
    availableShiftBoost,
    appliedShiftBoost,
    hasHoursRestrictionConflict,
    hasMarkedInterest: shift?.attributes.isInterested,
    isLoadingShiftModalsData: isLoading,
    modalRouteParams,
    navigateToModal,
    resolvedShiftQualification,
    searchMode,
    setBaseNavigationPathOverride,
    setInitialShiftModalsData,
    shift,
    shiftFailedLoading,
    shiftFailedReasons,
    shiftId,
    shiftSlots,
    friendsMap,
    recentColleaguesMap,
    outboundFriendRequestsSet,
    worker,
    computedArrivalData,
    hasPreShiftOrientation,
    preShiftOrientationAdjustedStart,
    preShiftOrientationLengthInMinutes,
    isIndependentContractorBadgesRequired,
    cancellationProtection,
    isCancellationProtectionPremiumApplied,
    isLoadingWorkplace,
    arrivalTimePoints,
    bundle,
    bundleBookingResults,
    bundleShiftPays,
    isBundleLoading: !isBundleAlreadyInContextData && isBundleLoading,
    double,
    isDoubleLoading: !isDoubleAlreadyInContextData && isDoubleLoading,
    companionBookabilityStatuses,
    isForYouShift: initialShiftModalsData?.isForYouShift ?? false,
    isTopThreeShift: initialShiftModalsData?.isTopThreeShift ?? false,
    doubleBookingResults,
    stateTransitionTimings,
  };
}

export type NavigateToModalFunction = ReturnType<typeof useShiftModalsData>["navigateToModal"];

export const [ShiftModalsDataProvider, useShiftModalsDataContext] = constate(useShiftModalsData);
/* eslint-enable max-lines */
